import { useCallback, useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  reload: () => void;
  setData: (updater: (prev: T | null) => T | null) => void;
}

/**
 * The console's read-path primitive: run an async loader on mount (and on any change
 * to `deps`), tracking loading/error, with `reload` for manual refresh and `setData`
 * for optimistic local updates after a write. Aborts stale results so a fast
 * reload can never be overwritten by a slower in-flight response.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (live) setData(result);
      })
      .catch((err: unknown) => {
        if (live) setError(messageOf(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const update = useCallback((updater: (prev: T | null) => T | null) => {
    setData((prev) => updater(prev));
  }, []);

  return { data, loading, error, reload, setData: update };
}
